import React, { useRef, useEffect, MutableRefObject } from 'react';
import { PitchResult } from '../utils/pitchDetection';
import { FrameStore } from '../utils/frameStore';
import './CombinedView.css';

export type RangeMode = 'wide' | 'standard' | 'vocal' | 'guitar' | 'follow';

/** Live / paused state of the tracker. Mutated by App, read every frame here. */
export interface ViewState {
  paused: boolean;
  pauseTimeMs: number;   // performance.now() when paused
  scrubBackMs: number;   // how far behind the pause point the playhead sits
}

interface PitchSample { t: number; p: PitchResult | null; }
interface NoteSegment { hz: number; startMs: number; durationMs: number; }
interface ActiveScheme { segments: NoteSegment[]; totalDurationMs: number; }

/** One extra pitch trace (a second input source). */
export interface ExtraTrace {
  id: string;
  label: string;
  color: string;        // css rgb like "209, 125, 195" (no alpha)
  frames: FrameStore;
}

interface Props {
  framesRef: MutableRefObject<FrameStore | null>;
  framesHiRef: MutableRefObject<FrameStore | null>;
  spectrumRef: MutableRefObject<Float32Array | null>;
  spectrumHiRef: MutableRefObject<Float32Array | null>;
  pitchRef: MutableRefObject<PitchResult | null>;
  viewStateRef: MutableRefObject<ViewState>;
  /** Outputs: what the rail panels should analyse (live, paused frame, or hovered frame). */
  viewSpectrumRef: MutableRefObject<Float32Array | null>;
  viewSpectrumHiRef: MutableRefObject<Float32Array | null>;
  viewPitchRef: MutableRefObject<PitchResult | null>;
  sampleRate: number;
  binCount: number;
  rangeMode: RangeMode;
  extraTracesRef?: MutableRefObject<ExtraTrace[]>;
  /** Live formant frequencies [F1, F2, F3] for the playhead ticks. */
  formantsRef?: MutableRefObject<number[] | null>;
  pianoKeysRef?: MutableRefObject<ReadonlySet<number>>;
  ghostPitchesRef?: MutableRefObject<PitchSample[] | null>;
  recStartMsRef?: MutableRefObject<number>;
  activeSchemeRef?: MutableRefObject<ActiveScheme | null>;
  schemePlayStartMsRef?: MutableRefObject<number | null>;
  schemeLoopingRef?: MutableRefObject<boolean>;
  metronomeActiveRef?: MutableRefObject<boolean>;
  bpmRef?: MutableRefObject<number>;
  timeSigRef?: MutableRefObject<{ num: number; den: number }>;
  metroStartMsRef?: MutableRefObject<number | null>;
  hoveredKeyRef?: MutableRefObject<number | null>;
  playbackGhostRef?: MutableRefObject<{ pitches: PitchSample[]; audio: HTMLAudioElement } | null>;
}

const W = 1200;
const H = 400;
const PLAYHEAD = 1000;
const MIN_DB = -100;
const MAX_DB = -10;
// Horizontal scale: 0.12 px per ms → 8.3 s visible left of the playhead.
export const PX_PER_MS = 0.12;
const GAP_MS = 250; // a hole in the frame store longer than this paints black

const NOTE_NAMES = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];

const RANGE_PRESETS: Record<Exclude<RangeMode, 'follow'>, { minHz: number; maxHz: number }> = {
  wide:     { minHz: 60,  maxHz: 3000 },
  standard: { minHz: 130, maxHz: 1500 },
  vocal:    { minHz: 100, maxHz: 800  },
  // Low E (82 Hz, with room for drop D at 73) up to the top of the high e string.
  guitar:   { minHz: 70,  maxHz: 1400 },
};

// Standard-tuning open strings, low to high. Drawn as faint markers in guitar range.
const GUITAR_STRINGS: { name: string; hz: number }[] = [
  { name: 'E', hz: 82.41 }, { name: 'A', hz: 110.0 }, { name: 'D', hz: 146.83 },
  { name: 'G', hz: 196.0 }, { name: 'B', hz: 246.94 }, { name: 'e', hz: 329.63 },
];

const SEMITONE_LINES: { hz: number; isC: boolean; label?: string }[] = (() => {
  const lines = [];
  for (let midi = 36; midi <= 108; midi++) {
    const hz = 440 * Math.pow(2, (midi - 69) / 12);
    const noteIdx = midi % 12;
    const octave = Math.floor(midi / 12) - 1;
    const isC = noteIdx === 0;
    const isLabeled = isC || noteIdx === 7;
    lines.push({ hz, isC, label: isLabeled ? `${NOTE_NAMES[noteIdx]}${octave}` : undefined });
  }
  return lines;
})();

function hzToY(hz: number, minHz: number, logRange: number): number {
  if (hz <= minHz) return H - 1;
  return H - 1 - (Math.log(hz / minHz) / logRange) * (H - 1);
}

function yToHz(y: number, minHz: number, logRange: number): number {
  return minHz * Math.exp((1 - y / (H - 1)) * logRange);
}

function dbToRgb(db: number): [number, number, number] {
  const t = Math.max(0, Math.min(1, (db - MIN_DB) / (MAX_DB - MIN_DB)));
  if (t < 0.25) {
    const s = t / 0.25;
    return [0, 10 * s | 0, 40 * s | 0];
  } else if (t < 0.5) {
    const s = (t - 0.25) / 0.25;
    return [20 * s | 0, (10 + 80 * s) | 0, (40 + 120 * s) | 0];
  } else if (t < 0.75) {
    const s = (t - 0.5) / 0.25;
    return [(20 + 180 * s) | 0, (90 + 100 * s) | 0, (160 - 80 * s) | 0];
  } else {
    const s = (t - 0.75) / 0.25;
    return [(200 + 55 * s) | 0, (190 + 65 * s) | 0, (80 + 175 * s) | 0];
  }
}

// 256-entry colour table for the 8-bit stored spectra.
const LUT = new Uint8Array(256 * 3);
for (let i = 0; i < 256; i++) {
  const [r, g, b] = dbToRgb(MIN_DB + (i / 255) * (MAX_DB - MIN_DB));
  LUT[i * 3] = r; LUT[i * 3 + 1] = g; LUT[i * 3 + 2] = b;
}

function hzToNoteName(hz: number): string {
  const midi = Math.round(12 * Math.log2(hz / 440) + 69);
  return `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;
}

function hzToPitchResult(hz: number, clarity: number): PitchResult {
  const midi = 12 * Math.log2(hz / 440) + 69;
  const rounded = Math.round(midi);
  return {
    hz: Math.round(hz * 10) / 10,
    note: NOTE_NAMES[((rounded % 12) + 12) % 12],
    octave: Math.floor(rounded / 12) - 1,
    cents: Math.round((midi - rounded) * 100),
    clarity,
  };
}

function drawGuitarStrings(ctx: CanvasRenderingContext2D, minHz: number, maxHz: number, logRange: number) {
  ctx.save();
  ctx.setLineDash([1, 3]);
  ctx.lineWidth = 1;
  ctx.font = 'bold 11px monospace';
  for (const { name, hz } of GUITAR_STRINGS) {
    if (hz < minHz || hz > maxHz) continue;
    const yp = Math.round(hzToY(hz, minHz, logRange));
    ctx.strokeStyle = 'rgba(201, 160, 58, 0.35)';
    ctx.beginPath(); ctx.moveTo(36, yp); ctx.lineTo(W, yp); ctx.stroke();
    ctx.fillStyle = 'rgba(227, 185, 85, 0.75)';
    ctx.fillText(`${name} ○`, W - 28, yp + 4);
  }
  ctx.restore();
}

/** Draw a smoothed pitch trace from a FrameStore, time-aligned to viewTime. */
function drawStoreTrace(
  ctx: CanvasRenderingContext2D, store: FrameStore, viewTime: number,
  minHz: number, maxHz: number, logRange: number,
  color: string, glow: string, width: number,
) {
  const windowMs = PLAYHEAD / PX_PER_MS;
  let j = store.indexAtTime(viewTime - windowMs);
  if (j < 0) j = 0;
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.shadowColor = glow;
  ctx.shadowBlur = 7;
  ctx.setLineDash([]);
  ctx.beginPath();
  let drawing = false;
  let prevX = 0, prevY = 0, prevT = 0;
  for (; j < store.length; j++) {
    const t = store.timeAt(j);
    if (t > viewTime) break;
    const hz = store.hzAt(j);
    const x = PLAYHEAD - (viewTime - t) * PX_PER_MS;
    if (hz <= 0 || hz < minHz || hz > maxHz || x < 0 || (drawing && t - prevT > GAP_MS)) { drawing = false; prevT = t; continue; }
    const y = hzToY(hz, minHz, logRange);
    if (!drawing) { ctx.moveTo(x, y); drawing = true; }
    else ctx.quadraticCurveTo(prevX, prevY, (prevX + x) / 2, (prevY + y) / 2);
    prevX = x; prevY = y; prevT = t;
  }
  if (drawing) ctx.lineTo(prevX, prevY);
  ctx.stroke();
  ctx.shadowBlur = 0;
}

export default function CombinedView({
  framesRef, framesHiRef, spectrumRef, spectrumHiRef, pitchRef,
  viewStateRef, viewSpectrumRef, viewSpectrumHiRef, viewPitchRef,
  sampleRate, binCount, rangeMode,
  extraTracesRef, formantsRef, pianoKeysRef, ghostPitchesRef, recStartMsRef,
  activeSchemeRef, schemePlayStartMsRef, schemeLoopingRef,
  metronomeActiveRef, bpmRef, timeSigRef, metroStartMsRef,
  hoveredKeyRef, playbackGhostRef,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<ImageData | null>(null);
  const viewRef = useRef({ minHz: 130, maxHz: 1500 });
  const rangeModeRef = useRef(rangeMode);
  const framesOutsideRef = useRef(0);
  const hoverXRef = useRef<number | null>(null);
  const rowBinRef = useRef<{ minHz: number; maxHz: number; bins: Int32Array } | null>(null);
  const scratchRef = useRef<Float32Array | null>(null);
  const scratchHiRef = useRef<Float32Array | null>(null);

  useEffect(() => {
    rangeModeRef.current = rangeMode;
    if (rangeMode !== 'follow') {
      viewRef.current = { ...RANGE_PRESETS[rangeMode] };
    } else {
      const p = pitchRef.current;
      if (p && p.hz > 30) {
        viewRef.current = {
          minHz: Math.max(30, p.hz / Math.pow(2, 1.5)),
          maxHz: Math.min(6000, p.hz * Math.pow(2, 1.5)),
        };
      }
    }
    framesOutsideRef.current = 0;
  }, [rangeMode, pitchRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let animId: number;

    const onMove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      hoverXRef.current = ((e.clientX - r.left) / r.width) * W;
    };
    const onLeave = () => { hoverXRef.current = null; };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    const draw = () => {
      const mode = rangeModeRef.current;
      const vs = viewStateRef.current;
      const now = performance.now();
      const viewTime = vs.paused ? vs.pauseTimeMs - vs.scrubBackMs : now;
      const store = framesRef.current;
      const storeHi = framesHiRef.current;

      // Follow mode re-centres on the live pitch (not while paused)
      if (mode === 'follow' && !vs.paused) {
        const pitch = pitchRef.current;
        if (pitch && pitch.hz > 30) {
          const { minHz, maxHz } = viewRef.current;
          const logRange = Math.log(maxHz / minHz);
          const normY = Math.log(pitch.hz / minHz) / logRange;
          if (normY < 0.25 || normY > 0.75) {
            framesOutsideRef.current++;
            if (framesOutsideRef.current >= 8) {
              framesOutsideRef.current = 0;
              viewRef.current = {
                minHz: Math.max(30, pitch.hz / Math.pow(2, 1.5)),
                maxHz: Math.min(6000, pitch.hz * Math.pow(2, 1.5)),
              };
            }
          } else framesOutsideRef.current = 0;
        } else framesOutsideRef.current = 0;
      }

      const { minHz, maxHz } = viewRef.current;
      const logRange = Math.log(maxHz / minHz);

      // Row → bin lookup, rebuilt when the range changes
      let rb = rowBinRef.current;
      if (!rb || rb.minHz !== minHz || rb.maxHz !== maxHz || rb.bins.length !== H) {
        const bins = new Int32Array(H);
        const nyquist = sampleRate / 2;
        for (let y = 0; y < H; y++) {
          const hz = yToHz(y, minHz, logRange);
          bins[y] = Math.min((hz / nyquist * binCount) | 0, binCount - 1);
        }
        rb = { minHz, maxHz, bins };
        rowBinRef.current = rb;
      }
      const rowBin = rb.bins;

      if (!imgRef.current) imgRef.current = ctx.createImageData(W, H);
      const img = imgRef.current;
      const data = img.data;

      // ── Spectrogram from the frame store, time-aligned ──
      const windowMs = PLAYHEAD / PX_PER_MS;
      let j = store ? store.indexAtTime(viewTime - windowMs) : -1;
      if (store && j < 0 && store.length > 0) j = 0;
      const storeBins = store ? store.bins : 0;
      for (let x = 0; x < PLAYHEAD; x++) {
        const colTime = viewTime - (PLAYHEAD - x) / PX_PER_MS;
        let row: Uint8Array | null = null;
        if (store && j >= 0) {
          while (j + 1 < store.length && store.timeAt(j + 1) <= colTime) j++;
          const t = store.timeAt(j);
          if (t <= colTime && colTime - t < GAP_MS) row = store.rowAt(j);
        }
        if (row && storeBins === binCount) {
          for (let y = 0; y < H; y++) {
            const v = row[rowBin[y]] * 3;
            const idx = (y * W + x) * 4;
            data[idx] = LUT[v]; data[idx + 1] = LUT[v + 1]; data[idx + 2] = LUT[v + 2]; data[idx + 3] = 255;
          }
        } else {
          for (let y = 0; y < H; y++) {
            const idx = (y * W + x) * 4;
            data[idx] = 5; data[idx + 1] = 5; data[idx + 2] = 20; data[idx + 3] = 255;
          }
        }
      }
      // Future area right of the playhead
      for (let x = PLAYHEAD; x < W; x++) {
        for (let y = 0; y < H; y++) {
          const idx = (y * W + x) * 4;
          data[idx] = 4; data[idx + 1] = 4; data[idx + 2] = 12; data[idx + 3] = 255;
        }
      }
      ctx.putImageData(img, 0, 0);

      // ── Decide which frame the rail panels analyse ──
      const hoverX = hoverXRef.current;
      let inspectTime: number | null = null;
      if (hoverX !== null && hoverX >= 0 && hoverX <= PLAYHEAD) inspectTime = viewTime - (PLAYHEAD - hoverX) / PX_PER_MS;
      else if (vs.paused) inspectTime = viewTime;

      if (inspectTime !== null && store && store.length > 0) {
        const fi = store.indexAtTime(inspectTime);
        if (fi >= 0 && inspectTime - store.timeAt(fi) < GAP_MS) {
          scratchRef.current = store.dequantize(fi, scratchRef.current ?? undefined);
          viewSpectrumRef.current = scratchRef.current;
          const hz = store.hzAt(fi);
          viewPitchRef.current = hz > 0 ? hzToPitchResult(hz, store.clarityAt(fi)) : null;
        } else {
          viewSpectrumRef.current = null;
          viewPitchRef.current = null;
        }
        if (storeHi && storeHi.length > 0) {
          const hj = storeHi.indexAtTime(inspectTime);
          if (hj >= 0 && inspectTime - storeHi.timeAt(hj) < GAP_MS * 2) {
            scratchHiRef.current = storeHi.dequantize(hj, scratchHiRef.current ?? undefined);
            viewSpectrumHiRef.current = scratchHiRef.current;
          } else viewSpectrumHiRef.current = null;
        }
      } else {
        viewSpectrumRef.current = spectrumRef.current;
        viewSpectrumHiRef.current = spectrumHiRef.current;
        viewPitchRef.current = pitchRef.current;
      }

      // Playhead fade + cursor
      const fade = ctx.createLinearGradient(PLAYHEAD - 30, 0, PLAYHEAD + 12, 0);
      fade.addColorStop(0, 'rgba(5,5,16,0)');
      fade.addColorStop(1, 'rgba(5,5,16,0.92)');
      ctx.fillStyle = fade;
      ctx.fillRect(PLAYHEAD - 30, 0, 42, H);
      ctx.strokeStyle = vs.paused ? 'rgba(227,185,85,0.55)' : 'rgba(127,184,221,0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 6]);
      ctx.beginPath(); ctx.moveTo(PLAYHEAD, 0); ctx.lineTo(PLAYHEAD, H); ctx.stroke();
      ctx.setLineDash([]);

      // Metronome beat ticks, phase-accurate from metroStartMsRef
      const metroActive = metronomeActiveRef?.current;
      const bpm = bpmRef?.current ?? 120;
      const metroStartMs = metroStartMsRef?.current ?? null;
      const timeSig = timeSigRef?.current ?? { num: 4, den: 4 };
      if (metroActive && bpm > 0 && metroStartMs !== null) {
        const beatMs = 60000 / bpm;
        const elapsed = viewTime - metroStartMs;
        const nMin = Math.max(0, Math.floor((elapsed - PLAYHEAD / PX_PER_MS) / beatMs));
        const nMax = Math.ceil((elapsed + (W - PLAYHEAD) / PX_PER_MS) / beatMs);
        ctx.setLineDash([2, 5]);
        for (let n = nMin; n <= nMax; n++) {
          const x = PLAYHEAD - (elapsed - n * beatMs) * PX_PER_MS;
          if (x < 0 || x > W) continue;
          const isDownbeat = n % timeSig.num === 0;
          ctx.lineWidth = isDownbeat ? 2 : 1;
          ctx.strokeStyle = isDownbeat ? 'rgba(201, 160, 58, 0.55)' : 'rgba(180, 145, 55, 0.22)';
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
          if (isDownbeat) {
            ctx.setLineDash([]);
            ctx.fillStyle = 'rgba(217, 168, 61, 0.6)';
            ctx.fillRect(x - 2, 0, 4, 6);
            ctx.setLineDash([2, 5]);
          }
        }
        ctx.setLineDash([]);
      }

      // Semitone grid
      ctx.font = '11px monospace';
      for (const { hz, isC, label } of SEMITONE_LINES) {
        if (hz < minHz || hz > maxHz) continue;
        const yp = Math.round(hzToY(hz, minHz, logRange));
        ctx.strokeStyle = isC ? 'rgba(122,109,140,0.6)' : 'rgba(61,53,72,0.35)';
        ctx.lineWidth = isC ? 0.8 : 0.4;
        ctx.beginPath(); ctx.moveTo(label ? 36 : 0, yp); ctx.lineTo(W, yp); ctx.stroke();
        if (label) {
          ctx.fillStyle = isC ? 'rgba(192,224,243,0.9)' : 'rgba(122,109,140,0.7)';
          ctx.fillText(label, 2, yp + 4);
        }
      }

      if (mode === 'guitar') drawGuitarStrings(ctx, minHz, maxHz, logRange);

      // Piano key reference lines — chartreuse horizontal
      const pianoKeys = pianoKeysRef?.current;
      if (pianoKeys && pianoKeys.size > 0) {
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        for (const midi of Array.from(pianoKeys)) {
          const hz = 440 * Math.pow(2, (midi - 69) / 12);
          if (hz < minHz || hz > maxHz) continue;
          const yp = Math.round(hzToY(hz, minHz, logRange));
          ctx.strokeStyle = 'rgba(208, 224, 122, 0.75)';
          ctx.beginPath(); ctx.moveTo(0, yp); ctx.lineTo(W, yp); ctx.stroke();
          ctx.font = 'bold 13px monospace';
          ctx.fillStyle = 'rgba(208, 224, 122, 0.9)';
          ctx.fillText(`${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`, W - 40, yp - 3);
        }
        ctx.setLineDash([]);
      }

      // Hovered piano key — silent cerulean reference line
      const hoveredKey = hoveredKeyRef?.current ?? null;
      if (hoveredKey !== null) {
        const hHz = 440 * Math.pow(2, (hoveredKey - 69) / 12);
        if (hHz >= minHz && hHz <= maxHz) {
          const yp = Math.round(hzToY(hHz, minHz, logRange));
          ctx.strokeStyle = 'rgba(127, 184, 221, 0.6)';
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 5]);
          ctx.beginPath(); ctx.moveTo(0, yp); ctx.lineTo(W, yp); ctx.stroke();
          ctx.setLineDash([]);
          ctx.font = 'bold 12px monospace';
          ctx.fillStyle = 'rgba(192, 224, 243, 0.9)';
          ctx.fillText(hzToNoteName(hHz), W - 70, yp - 3);
        }
      }

      // Active sequence bars (karaoke-style piano roll)
      const activeScheme = activeSchemeRef?.current;
      const playStartMs = schemePlayStartMsRef?.current ?? null;
      if (activeScheme && playStartMs !== null) {
        const elapsed = viewTime - playStartMs;
        const D = activeScheme.totalDurationMs;
        const looping = schemeLoopingRef?.current ?? false;
        const loopElapsed = D > 0 && looping ? elapsed % D : Math.min(elapsed, D || elapsed);
        const iterOffsets = looping && D > 0 ? [-D, 0, D] : [0];
        const BAR_H = 6;
        for (const iterOff of iterOffsets) {
          for (const seg of activeScheme.segments) {
            const relMs = seg.startMs - loopElapsed + iterOff;
            const startX = PLAYHEAD + relMs * PX_PER_MS;
            const endX = startX + seg.durationMs * PX_PER_MS;
            if (endX < 0 || startX > W) continue;
            const y = Math.round(hzToY(seg.hz, minHz, logRange));
            const isCurrent = relMs <= 0 && relMs > -seg.durationMs;
            const isFuture = relMs > 0;
            const cx = Math.max(0, startX);
            const cw = Math.min(W, endX) - cx;
            if (cw <= 0) continue;
            ctx.fillStyle = isCurrent ? 'rgba(227, 185, 85, 0.95)' : isFuture ? 'rgba(201, 160, 58, 0.7)' : 'rgba(107, 82, 25, 0.28)';
            ctx.fillRect(cx, y - BAR_H / 2, cw, BAR_H);
            if (isFuture && startX < W - 20 && seg.durationMs * PX_PER_MS > 14) {
              ctx.font = '10px monospace';
              ctx.fillStyle = 'rgba(217, 168, 61, 0.9)';
              ctx.fillText(hzToNoteName(seg.hz), Math.max(startX + 2, 2), y - 5);
            }
          }
        }
      }

      // Current note zone highlight ±50 cents (for the frame under inspection)
      const pitch = viewPitchRef.current;
      if (pitch && pitch.hz >= minHz && pitch.hz <= maxHz) {
        const midi = Math.round(12 * Math.log2(pitch.hz / 440) + 69);
        const noteHz = 440 * Math.pow(2, (midi - 69) / 12);
        const yTop = hzToY(noteHz * Math.pow(2,  50 / 1200), minHz, logRange);
        const yBot = hzToY(noteHz * Math.pow(2, -50 / 1200), minHz, logRange);
        const absCents = Math.abs(pitch.cents);
        const zoneAlpha = absCents <= 15 ? 0.09 : absCents <= 35 ? 0.05 : 0.02;
        ctx.fillStyle = `rgba(80,255,140,${zoneAlpha})`;
        ctx.fillRect(0, yTop, W, yBot - yTop);
      }

      // Playback ghost — reference take's contour aligned to its own playback time
      const pbGhost = playbackGhostRef?.current;
      if (pbGhost && !pbGhost.audio.paused) {
        const nowMs = pbGhost.audio.currentTime * 1000;
        const minT = nowMs - PLAYHEAD / PX_PER_MS;
        const maxT = nowMs + (W - PLAYHEAD) / PX_PER_MS;
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = 'rgba(209, 125, 195, 0.9)';
        ctx.shadowColor = 'rgba(172, 78, 151, 0.6)';
        ctx.shadowBlur = 9;
        ctx.setLineDash([]);
        ctx.beginPath();
        let drawingPb = false;
        let pbPrevX = 0, pbPrevY = 0;
        for (let i = 0; i < pbGhost.pitches.length; i++) {
          const { t, p } = pbGhost.pitches[i];
          if (t < minT) { drawingPb = false; continue; }
          if (t > maxT) break;
          if (!p || p.hz < minHz || p.hz > maxHz) { drawingPb = false; continue; }
          const x = PLAYHEAD - (nowMs - t) * PX_PER_MS;
          const y = hzToY(p.hz, minHz, logRange);
          if (!drawingPb) { ctx.moveTo(x, y); drawingPb = true; }
          else ctx.quadraticCurveTo(pbPrevX, pbPrevY, (pbPrevX + x) / 2, (pbPrevY + y) / 2);
          pbPrevX = x; pbPrevY = y;
        }
        if (drawingPb) ctx.lineTo(pbPrevX, pbPrevY);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Recording ghost — the reference take laid over the take in progress,
      // aligned by time since the record button was pressed.
      const ghostSamples = ghostPitchesRef?.current;
      const recStart = recStartMsRef?.current ?? null;
      if (ghostSamples && recStart !== null) {
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = 'rgba(209, 125, 195, 0.9)';
        ctx.shadowColor = 'rgba(172, 78, 151, 0.6)';
        ctx.shadowBlur = 9;
        ctx.setLineDash([]);
        ctx.beginPath();
        let ghostDrawing = false;
        let gpX = 0, gpY = 0;
        for (let i = 0; i < ghostSamples.length; i++) {
          const { t, p } = ghostSamples[i];
          const x = PLAYHEAD - (viewTime - (recStart + t)) * PX_PER_MS;
          if (x < 0) { ghostDrawing = false; continue; }
          if (x > W) break;
          if (!p || p.hz < minHz || p.hz > maxHz) { ghostDrawing = false; continue; }
          const y = hzToY(p.hz, minHz, logRange);
          if (!ghostDrawing) { ctx.moveTo(x, y); ghostDrawing = true; }
          else ctx.quadraticCurveTo(gpX, gpY, (gpX + x) / 2, (gpY + y) / 2);
          gpX = x; gpY = y;
        }
        if (ghostDrawing) ctx.lineTo(gpX, gpY);
        ctx.stroke();
        ctx.shadowBlur = 0;
        if (ghostDrawing) {
          ctx.font = 'bold 11px monospace';
          ctx.fillStyle = 'rgba(209, 125, 195, 0.9)';
          ctx.fillText('ref', Math.min(gpX, PLAYHEAD) + 4, gpY + 4);
        }
      }

      // Extra sources' traces
      const extras = extraTracesRef?.current ?? [];
      for (const ex of extras) {
        drawStoreTrace(ctx, ex.frames, viewTime, minHz, maxHz, logRange,
          `rgba(${ex.color}, 0.9)`, `rgba(${ex.color}, 0.5)`, 2.5);
        // Label at the trace's newest point near the playhead
        const n = ex.frames.length;
        if (n > 0) {
          const hz = ex.frames.hzAt(n - 1);
          if (hz > 0 && hz >= minHz && hz <= maxHz) {
            ctx.font = 'bold 11px monospace';
            ctx.fillStyle = `rgba(${ex.color}, 0.95)`;
            ctx.fillText(ex.label, PLAYHEAD + 4, hzToY(hz, minHz, logRange) + 4);
          }
        }
      }

      // Primary live pitch trace — green
      if (store) {
        drawStoreTrace(ctx, store, viewTime, minHz, maxHz, logRange,
          'rgba(70, 255, 130, 0.92)', 'rgba(70, 255, 130, 0.55)', 3);
      }

      // Formant ticks at the playhead (violet), live only
      const fm = !vs.paused ? formantsRef?.current : null;
      if (fm) {
        ctx.font = 'bold 10px monospace';
        for (let i = 0; i < fm.length; i++) {
          const f = fm[i];
          if (f < minHz || f > maxHz) continue;
          const y = Math.round(hzToY(f, minHz, logRange));
          ctx.fillStyle = 'rgba(209, 125, 195, 0.85)';
          ctx.fillRect(PLAYHEAD - 14, y - 1, 14, 2);
          ctx.fillText(`F${i + 1}`, PLAYHEAD + 3, y + 4);
        }
      }

      // Pitch marker + label for the inspected frame
      if (pitch && pitch.hz >= minHz && pitch.hz <= maxHz) {
        const y = Math.round(hzToY(pitch.hz, minHz, logRange));
        ctx.strokeStyle = 'rgba(217, 168, 61, 0.6)';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 4]);
        ctx.beginPath(); ctx.moveTo(40, y); ctx.lineTo(W, y); ctx.stroke();
        ctx.setLineDash([]);
        const centsStr = pitch.cents > 0 ? `+${pitch.cents}¢` : `${pitch.cents}¢`;
        const label = `${pitch.note}${pitch.octave}  ${pitch.hz}Hz  ${centsStr}`;
        ctx.font = 'bold 19px monospace';
        ctx.fillStyle = 'rgba(227, 185, 85, 0.95)';
        const tw = ctx.measureText(label).width;
        const labelX = Math.min(PLAYHEAD - tw - 10, W - tw - 6);
        const labelY = y > 22 ? y - 6 : y + 22;
        ctx.fillText(label, labelX, labelY);
      }

      // Hover inspection cursor
      if (hoverX !== null && hoverX >= 0 && hoverX <= PLAYHEAD) {
        ctx.strokeStyle = 'rgba(192, 224, 243, 0.7)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(hoverX, 0); ctx.lineTo(hoverX, H); ctx.stroke();
        ctx.setLineDash([]);
        const back = (PLAYHEAD - hoverX) / PX_PER_MS / 1000;
        ctx.font = 'bold 11px monospace';
        ctx.fillStyle = 'rgba(192, 224, 243, 0.9)';
        const txt = `-${back.toFixed(2)}s`;
        const tx = hoverX + 6 + ctx.measureText(txt).width > W ? hoverX - 6 - ctx.measureText(txt).width : hoverX + 6;
        ctx.fillText(txt, tx, 14);
      }

      // Range / state indicator
      ctx.font = '10px monospace';
      ctx.fillStyle = vs.paused ? 'rgba(227,185,85,0.9)' : 'rgba(122,109,140,0.7)';
      const stateTxt = vs.paused ? `PAUSED  -${(vs.scrubBackMs / 1000).toFixed(1)}s   ` : '';
      ctx.fillText(`${stateTxt}${mode}  ${Math.round(minHz)}–${Math.round(maxHz)} Hz`, 4, H - 4);

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animId);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      imgRef.current = null;
      rowBinRef.current = null;
    };
  }, [framesRef, framesHiRef, spectrumRef, spectrumHiRef, pitchRef,
      viewStateRef, viewSpectrumRef, viewSpectrumHiRef, viewPitchRef,
      sampleRate, binCount, extraTracesRef, formantsRef,
      pianoKeysRef, ghostPitchesRef, recStartMsRef,
      activeSchemeRef, schemePlayStartMsRef, schemeLoopingRef,
      metronomeActiveRef, bpmRef, timeSigRef, metroStartMsRef, hoveredKeyRef,
      playbackGhostRef]);

  return (
    <div className="combined-view">
      <div className="combined-label">Spectrogram + Pitch</div>
      <canvas ref={canvasRef} width={W} height={H} style={{ cursor: 'crosshair' }} />
    </div>
  );
}
